import { TaskLanguageSelect, TaskNotice, TaskSetup } from '@/features/contribution-kit';
import { TranscriptionSourcePicker } from '@/components/contribute/TranscriptionSourcePicker';
import { WriteModeSwitch } from '@/components/contribute/WriteModeSwitch';
import type { TranscriptionSource } from '@/lib/transcription-source';
import type { TranscriptionAudioCopy } from './transcription-audio-copy';

type TranscriptionAudioSetupProps = {
  t: Record<string, unknown>;
  copy: TranscriptionAudioCopy;
  languages: Array<{ code: string; name: string; displayName?: string }>;
  selectedLanguage: string;
  onLanguageChange: (code: string) => void;
  source: TranscriptionSource;
  onSourceChange: (source: TranscriptionSource) => void;
  sessionResumeActive: boolean;
  isLoading: boolean;
  onStart: () => void;
  onBack: () => void;
};

/** Before a "Transcrire" session: writing mode, language, which audios, rules, start. */
export function TranscriptionAudioSetup({
  t,
  copy,
  languages,
  selectedLanguage,
  onLanguageChange,
  source,
  onSourceChange,
  sessionResumeActive,
  isLoading,
  onStart,
  onBack,
}: TranscriptionAudioSetupProps) {
  const s = (key: string) => String(t[key] ?? '');
  return (
    <TaskSetup
      task="transcribe"
      title={s('transcribeAudioIntroTitle')}
      description={s('transcribeAudioIntroDesc')}
      instructions={[...copy.instructions]}
      notices={
        sessionResumeActive && selectedLanguage ? (
          <TaskNotice tone="info" title={copy.resumeTitle}>
            {copy.resumeBody}
          </TaskNotice>
        ) : null
      }
      startLabel={s('loadAudios')}
      onStart={onStart}
      startDisabled={!selectedLanguage}
      starting={isLoading}
      onBack={onBack}
    >
      <WriteModeSwitch active="transcribe" className="w-full sm:grid" />
      <div className="space-y-2">
        <label htmlFor="transcription-audio-language" className="text-sm font-medium text-foreground">
          {s('selectLanguage')}
        </label>
        <p className="text-sm leading-6 text-muted-foreground">{s('prepareAudioSessionDesc')}</p>
        <TaskLanguageSelect
          id="transcription-audio-language"
          value={selectedLanguage}
          onChange={onLanguageChange}
          languages={languages.map((language) => ({ ...language, flag: undefined }))}
          placeholder={s('selectLanguage')}
        />
      </div>
      <TranscriptionSourcePicker t={t} value={source} onChange={onSourceChange} />
    </TaskSetup>
  );
}
